import moment from 'moment';
import { omitBy, groupBy, omit } from 'lodash-es';
import { parseMinimumBreakLength } from './util/parse-minimum-break-length';
import type { Types } from '../../types';
import { parseGroupReferences } from './util/parse-group-references';
import { parseLocationReferences } from './util/parse-location-references';
import type { ConnectedTypes } from '../../make-connected';
import { getDayIndex, getVertexId } from '../../../core/util';
import { idOf } from './util/util';
import { parseDayIndices } from './util/parse-days';
import type { DateType } from '../../../common/types';
import { getDefaultInterval } from './intervals';
import { makeChainable } from '../../../common/make-chainable';

export function toDayAndStart (start: DateType) {
  return {
    start: parseFloat(moment.utc(start).format('HH.mm')),
    day:   getDayIndex(start)
  };
}

type _Event = Types.Entities.Event & {
  course?: null | ConnectedTypes.course;
};

// a type generic that makes certain properties of T required
type RequireProperties<T, K extends keyof T> = T & { [P in K]-?: NonNullable<T[P]> };

type Out =  {
  events:     Types.Entities.Event[];
  activities: Types.Entities.Activity[];
};
export function fromEvents (
  _events:  ConnectedTypes.event[],
  settings: ConnectedTypes.divisionSettings,
  options:  Types.parsedToOptions
): Out {
  const defaultInterval = getDefaultInterval(settings);

  const events = (_events ?? [])
    .map(event => {
      const id = getVertexId(event, options);

      const duration = event.preferredDuration;
      if (!duration) throw new Error(`(RS::To::fromEvents) Event "${id}" has no duration`);

      // allow duration variance only if the event isn't linked
      const maxDurationVariance = event.linkedEventsSet == null
        ? (event.durationVariance ?? event.course?.eventDurationVariance)
        : 0;

      const preferredTime = (() => {
        const centerOfAttraction = event.centerOfAttraction ?? event.course?.centerOfAttraction;
        if (!centerOfAttraction) return;
        return parseFloat(centerOfAttraction.replace(':', '.'));
      })();

      const period = (() => {
        const periodId = event.period ?? event.course?.period;
        if (!periodId) return;
        return getVertexId(periodId, options);
      })();

      const intervals = event.intervals ?? event.course?.intervals ?? defaultInterval;

      const doc: _Event = {
        id:                  idOf.event(event, options),
        duration:            duration,
        maxDurationVariance: maxDurationVariance,
        weight:              event.weight  ?? event.course?.weight,
        density:             event.density ?? event.course?.density,
        preferredTime:       preferredTime,
        period:              period,
        days:                parseDayIndices           (event.days ?? event.course?.days, settings),
        dependencies:        parseLocationReferences   (event.locations ?? event.course?.locations, options),
        groups:              parseGroupReferences      ({ type: 'event', item: event }, options),
        intervals:           idOf.intervalPairReference(intervals, undefined, options),
        blockedTimes:        (event.lockedTimes ?? event.course?.lockedTimes)?.map(x => idOf.lockedTime(x, options)),
        minBreakDuration:    parseMinimumBreakLength   (event.minBreakLength ?? event.course?.minBreakLength),
        distributionKey:     event.course?.id,

        // temporary data
        course: event.course,
      };

      // if the event has a fixed day and start
      if (event.fixedStart && event.start) {
        Object.assign(doc, toDayAndStart(event.start));
      }

      if (options.includeEntityMeta) {
        doc.name = event.displayName ?? event.course?.displayName;
      }

      ////
      //// filter events based on partialScheduleOptions
      ////
      if (options.partialScheduleOptions) {
        const { includedEvents, omittedEventsHandling } = options.partialScheduleOptions;
        if (includedEvents && !includedEvents.has(doc.id)) {  // collection.id  => take into account dynamic locked times too!

          if (omittedEventsHandling == 'ignore') return;

          if (omittedEventsHandling == 'freeze') {
            // must not be parked and have a start and duration to be frozen, otherwise it's ignored
            if (event.parked || !event.start || !event.duration) return;

            // fix day, start and end
            Object.assign(doc, toDayAndStart(event.start));
            doc.duration = event.duration;
            doc.maxDurationVariance = 0;

            // override intervals and days to not cause conflicts
            const numDays = settings.numDays ?? 5;
            doc.days      = Array.from({ length: numDays }, (_, i) => i);
            doc.intervals = Array.from({ length: numDays }, () => [{ start: 0, end: 24.00 }]);

            // fix locations
            doc.dependencies = (event.inLocations ?? [])
              .filter((x): x is NonNullable<typeof x> => !!x)
              .map(x => {
                const id = getVertexId(x, options);

                // NOTE: Frozen events should retain all locations references, even those that are explicitly excluded.

                return [{ dependency: id }] as Types.Shared.DependencyReference[];
              });
          }

        }
      }

      return omitBy(doc, x => x == null) as typeof doc;
    })
    .filter((x): x is NonNullable<typeof x> => !!x);

  const activities = makeChainable(events)
    .chain(x => x.filter((x): x is RequireProperties<typeof x, 'course'> => !!x.course))
    .chain(x => Object.entries(groupBy(x, x => getVertexId(x.course, options)))
      .map(([id, events]) => {
        const doc: Types.Entities.Activity = {
          id:     id,
          events: events.map(x => x.id)
        };

        if (options.includeEntityMeta) {
          doc.name = events[0].course?.displayName;
        }

        return doc;
      })
    ).value;

  // remove temporary data from events
  const outEvents = events.map(x => omit(x, 'course' satisfies keyof typeof x));

  return {
    events: outEvents,
    activities
  };
}